// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { INDUSTRY_FILTER_OPTIONS } from '../../config/configProductFilters';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';
import SaveProductButton from '../SaveProductButton/SaveProductButton';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const industryLabel = industry => {
  if (!industry) return null;
  const found = INDUSTRY_FILTER_OPTIONS.find(o => o.option === industry);
  return found?.label || industry;
};

/**
 * ListingCardImage
 * @component
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    badges,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {badges}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
    isFactoryProduct,
    moqLabel,
    categoryLabel,
    industryKey,
  } = translations;

  const classes = classNames(rootClassName || css.root, className, {
    [css.productCard]: isFactoryProduct,
  });

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const industry = industryLabel(industryKey);

  const imageBadges =
    isFactoryProduct && categoryLabel ? (
      <div className={css.imageBadges}>
        <span className={css.categoryBadge}>{categoryLabel}</span>
      </div>
    ) : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <div className={css.media}>
        {isFactoryProduct ? (
          <div className={css.saveBtnWrap}>
            <SaveProductButton listing={listing} variant="icon" />
          </div>
        ) : null}
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            aspectRatioClassName={aspectRatioClassName}
            lazyLoadImage={lazyLoadImage}
            badges={imageBadges}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={aspectRatioClassName}
            width={aspectWidth}
            height={aspectHeight}
            setActivePropsMaybe={setActivePropsMaybe}
          />
        )}
      </div>

      <div className={classNames(css.info, { [css.infoProduct]: isFactoryProduct })}>
        {isFactoryProduct ? (
          <div className={css.badgeRow}>
            {industry ? <span className={css.industryBadge}>{industry}</span> : null}
            {moqLabel ? (
              <span className={css.moqBadge}>
                {intl.formatMessage({ id: 'ListingCard.moq' }, { moq: moqLabel })}
              </span>
            ) : null}
          </div>
        ) : null}

        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}

        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}

          {showAuthorInfo ? (
            <div
              className={classNames(css.authorInfo, {
                [css.lightText]: darkMode,
              })}
            >
              <span>{authorName}</span>
            </div>
          ) : null}

          {isFactoryProduct ? (
            <div className={css.cardFooter}>
              <span className={css.metaHint}>
                <FormattedMessage id="ListingCard.viewToSource" />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
